import React, { useEffect, useRef, useState } from "react";
import { Pencil } from "lucide-react";

/**
 * Inline editable text. Click (or pencil icon) to edit, Enter/blur to save, Escape to cancel.
 * Props:
 *  - value: current string
 *  - onSave: async (newValue) => void. Caller handles toasts/errors.
 *  - testid: optional data-testid prefix
 *  - className: class applied to the text span
 *  - uppercase: if true, upper-cases the saved value
 *  - disabled: if true, renders plain text
 */
export default function InlineEdit({ value, onSave, testid, className = "", uppercase = false, disabled = false, placeholder = "" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ""); }, [value]);
  useEffect(() => { if (editing) { inputRef.current?.focus(); inputRef.current?.select(); } }, [editing]);

  const commit = async () => {
    const next = uppercase ? draft.trim().toUpperCase() : draft.trim();
    if (!next || next === (value || "").trim()) { setEditing(false); setDraft(value || ""); return; }
    setSaving(true);
    try { await onSave(next); setEditing(false); }
    catch (e) { /* caller toasts */ setDraft(value || ""); setEditing(false); }
    finally { setSaving(false); }
  };
  const cancel = () => { setDraft(value || ""); setEditing(false); };

  if (disabled) return <span className={className}>{value}</span>;
  if (!editing) {
    return (
      <button
        type="button"
        data-testid={testid ? `${testid}-view` : undefined}
        onClick={() => setEditing(true)}
        className={`group inline-flex items-center gap-1.5 text-left hover:text-blue-600 transition-colors ${className}`}
        title="Click para editar"
      >
        <span>{value || <span className="italic text-slate-400">{placeholder || "—"}</span>}</span>
        <Pencil size={11} className="opacity-0 group-hover:opacity-100 text-slate-400" />
      </button>
    );
  }
  return (
    <input
      ref={inputRef}
      data-testid={testid ? `${testid}-input` : undefined}
      value={draft}
      disabled={saving}
      onChange={(e) => setDraft(uppercase ? e.target.value.toUpperCase() : e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") cancel(); }}
      className={`px-2 py-0.5 rounded border border-blue-300 bg-white outline-none focus:ring-2 focus:ring-blue-200 ${className}`}
    />
  );
}
